import { defineStore } from "pinia";
import { GroupedUsers, User } from "@/types";
import { Auth } from "aws-amplify";
import CognitoIdentityServiceProvider, {
  GroupType,
  UserType,
} from "aws-sdk/clients/cognitoidentityserviceprovider";
import { sortBy } from "lodash-es";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { useGammaAuthenticator } from "@/composeable/gamma-authenticator";

export interface CognitoAdminState {
  showCustomAttributes: boolean;
  users: User[];
  organisationsList: string[];
  groups: string[];
  currentUser: string;
  currentUserGroups: string[];
  provider: CognitoIdentityServiceProvider | null;
  isAuthenticating: Promise<any> | null;
}

export const useCognitoAdmin = defineStore("cognitoAdmin", {
  state: (): CognitoAdminState => ({
    showCustomAttributes: false,
    users: [],
    organisationsList: [],
    groups: [],
    currentUser: "",
    currentUserGroups: [],
    provider: null,
    isAuthenticating: null,
  }),
  getters: {
    request(): { UserPoolId: string } {
      return { UserPoolId: (Auth as any)._config.userPoolId };
    },
  },
  actions: {
    async logout() {
      await useGammaAuthenticator().signOut();
      this.provider = null;
    },
    async currentCredentials() {
      return Auth.currentCredentials().then((r: any) => ({
        ...r,
        region: (Auth as any)._config["aws_cognito_region"],
      }));
    },
    async currentAuthenticatedUser() {
      return Auth.currentAuthenticatedUser();
    },
    async getProvider(): Promise<CognitoIdentityServiceProvider> {
      if (this.provider) {
        return Promise.resolve(this.provider);
      }
      const credentials = await this.currentCredentials();
      if (!credentials.sessionToken) {
        if (!this.isAuthenticating) {
          this.isAuthenticating = new Promise((resolve) => {
            setTimeout(() => {
              resolve(this.getProvider());
            }, 321);
          });
        }
        return this.isAuthenticating;
      } else {
        this.isAuthenticating = null;
        this.provider = new CognitoIdentityServiceProvider(credentials);
        return this.provider;
      }
    },
    async createUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminCreateUserRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminCreateUserResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminCreateUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminCreateUserRequest)
        .promise();
    },
    async loadUsers() {
      const visibleGroups = await this.getVisibleGroups();
      this.groups = visibleGroups.map((x) => x.GroupName || "");

      const groupedUsers = await Promise.all(
        visibleGroups.map(async (x) => ({
          group: x,
          users: await this.listUsersInGroup({
            ...this.request,
            GroupName: x.GroupName || "",
          }),
        }))
      );

      const groupUserMap = this.generateUsersGroupsMapping(groupedUsers);

      // ensures no duplicate will be added
      const userMap = groupedUsers
        .flatMap((x) => x.users)
        .reduce(
          (acc, cur) => acc.set(cur.Username || "", cur),
          new Map<string, UserType>()
        );

      const unsorted = Array.from(userMap.values()).map((x) =>
        this.toUser(x, groupUserMap.get(x.Username || "") || [])
      );
      this.users = sortBy(unsorted, (u) => u.username);
    },

    generateUsersGroupsMapping(
      groupedUsers: GroupedUsers[]
    ): Map<string, string[]> {
      return groupedUsers.reduce((groupMap, userGroup) => {
        for (const user of userGroup.users) {
          const userName = user.Username;
          if (!userName) continue;

          if (userName && groupMap.has(userName)) {
            const currentGroups = groupMap.get(userName);
            groupMap.set(userName, [
              ...(currentGroups || []),
              userGroup.group.GroupName || "",
            ]);
          } else {
            groupMap.set(userName, [userGroup.group.GroupName || ""]);
          }
        }

        return groupMap;
      }, new Map<string, string[]>());
    },

    async listUsersInGroup(
      request: CognitoIdentityServiceProvider.ListUsersInGroupRequest
    ): Promise<UserType[]> {
      const response = await (this.provider as CognitoIdentityServiceProvider)
        .listUsersInGroup(request)
        .promise();
      const users = response.Users || [];

      if (response.NextToken) {
        const otherUsers = await this.listUsersInGroup({
          ...request,
          NextToken: response.NextToken,
        });
        users.push(...otherUsers);
      }

      return users;
    },

    async getVisibleGroups(): Promise<GroupType[]> {
      const provider =
        (await this.getProvider()) as CognitoIdentityServiceProvider;

      const groups = (
        await provider.listGroups(this.request).promise()
      ).Groups?.filter((x) => typeof x.Precedence !== "undefined")?.sort(
        (a, b) => (a.Precedence || 0) - (b.Precedence || 0)
      );

      if (!groups) return [];

      const userGroupSet = new Set<string>(await this.getCurrentUserGroups());
      const userMostPrecedence = groups.filter((x) =>
        userGroupSet.has(x.GroupName || "")
      )[0];
      return groups.slice(groups.indexOf(userMostPrecedence));
    },

    toUser(cognitoUser: UserType, groups: string[]) {
      const organisationName =
        cognitoUser.Attributes?.find((x) => x.Name === "custom:organisation")
          ?.Value || "";
      const coverHolder =
        cognitoUser.Attributes?.find((x) => x.Name === "custom:cover_holder")
          ?.Value || "";

      const lastLoginAttr = cognitoUser.Attributes?.find(
        (x) => x.Name === "custom:last_login_timestamp"
      );
      return new User({
        username: cognitoUser.Username || "",
        email: cognitoUser.Attributes
          ? (cognitoUser.Attributes.filter((x) => x.Name === "email")[0]
              ?.Value as string)
          : "",
        role: "",
        groups,
        verifiedEmail:
          cognitoUser.Attributes?.filter((x) => x.Name === "email_verified")[0]
            ?.Value || "",
        organisationName,
        coverHolder,
        createdAt: cognitoUser.UserCreateDate?.toISOString(),
        updatedAt: cognitoUser.UserLastModifiedDate?.toISOString(),
        lastLogin: lastLoginAttr?.Value
          ? new Date(Number.parseInt(lastLoginAttr.Value))
          : undefined,
      });
    },
    async getUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminGetUserRequest>
    ): Promise<any> {
      const userGroups: any[] =
        (await this.provider
          ?.adminListGroupsForUser({
            ...this.request,
            ...request,
          } as CognitoIdentityServiceProvider.Types.AdminGetUserRequest)
          .promise()
          .then((response) => {
            return response.Groups?.map(
              ({ GroupName }) => GroupName
            ) as string[];
          })
          .catch(() => {
            return [] as string[];
          })) || [];

      await (this.provider as CognitoIdentityServiceProvider)
        .adminGetUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminGetUserRequest)
        .promise()
        .then((response) => {
          const organisationName =
            response.UserAttributes?.find(
              ({ Name }) => Name === "custom:organisation"
            )?.Value || "";
          const coverHolder =
            response.UserAttributes?.find(
              ({ Name }) => Name === "custom:cover_holder"
            )?.Value || "";
          const user = new User({
            username: response.Username || "",
            email: response.UserAttributes
              ? (response.UserAttributes.filter(
                  ({ Name }) => Name === "email"
                )[0].Value as string)
              : "",
            role: "",
            groups: userGroups,
            verifiedEmail:
              response.UserAttributes?.filter(
                ({ Name }) => Name === "email_verified"
              )[0].Value || "",
            organisationName,
            coverHolder,
            createdAt: response.UserCreateDate?.toISOString(),
            updatedAt: response.UserLastModifiedDate?.toISOString(),
          });

          if (this.users) {
            const index = this.users.findIndex(
              ({ username }) => username === request.Username
            );
            if (index > -1) {
              this.users.splice(index, 1, user);
            }
          }
        });
    },
    async updateUserAttributes(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminUpdateUserAttributes({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminUpdateUserAttributesRequest)
        .promise()
        .then((r) => {
          this.getUser({ Username: request.Username });
          return r;
        });
    },
    async getCurrentUser(): Promise<void> {
      return Auth.currentSession().then((session: CognitoUserSession) => {
        this.currentUser = session.getAccessToken().payload.username;
      });
    },
    async getCurrentUserGroups(): Promise<string[]> {
      await this.getProvider();
      const userCredentials = await this.currentAuthenticatedUser();
      this.currentUserGroups =
        userCredentials.signInUserSession.accessToken.payload["cognito:groups"];
      return this.currentUserGroups;
    },
    async addUserToGroup(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminAddUserToGroupRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminAddUserToGroup({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminAddUserToGroupRequest)
        .promise();
    },
    async removeUserFromGroup(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminRemoveUserFromGroupRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminRemoveUserFromGroup({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminRemoveUserFromGroupRequest)
        .promise();
    },
    async deleteUser(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminDeleteUserRequest>
    ): Promise<unknown> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminDeleteUser({
          ...this.request,
          ...request,
        } as CognitoIdentityServiceProvider.Types.AdminDeleteUserRequest)
        .promise()
        .then(() =>
          this.users.splice(
            this.users.findIndex(
              ({ username }) => username === request.Username
            ),
            1
          )
        );
    },
    async resetPassword(
      request: Partial<CognitoIdentityServiceProvider.Types.AdminResetUserPasswordRequest>
    ): Promise<CognitoIdentityServiceProvider.Types.AdminResetUserPasswordResponse> {
      await this.getProvider();
      return (this.provider as CognitoIdentityServiceProvider)
        .adminResetUserPassword({
          ...this.request,
          ...request,
          ClientMetadata: {
            Permanent: "--no-permanent",
          },
        } as CognitoIdentityServiceProvider.Types.AdminResetUserPasswordRequest)
        .promise();
    },
  },
});
